import React, { useState, useEffect } from 'react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import locationsService from '../../services/locationsService';
import NotificationToast from '../../components/ui/NotificationToast';

const Locations = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingLoc, setEditingLoc] = useState(null);
  const [notification, setNotification] = useState(null);
  const [formData, setFormData] = useState({ name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', is_head_office: false, status: 'active' });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const result = await locationsService.getLocations({ search: searchTerm });
      if (result?.success) setLocations(result.data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => loadLocations(), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = editingLoc
        ? await locationsService.updateLocation(editingLoc.id, formData)
        : await locationsService.createLocation(formData);
      
      if (result?.success) {
        setNotification({ message: `Location ${editingLoc ? 'updated' : 'created'} successfully`, type: 'success' });
        setShowModal(false);
        setEditingLoc(null);
        setFormData({ name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', is_head_office: false, status: 'active' });
        loadLocations();
      } else {
        setNotification({ message: result?.error || 'Operation failed', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  const handleEdit = (loc) => {
    setEditingLoc(loc);
    setFormData({ name: loc.name, address: loc.address || '', city: loc.city || '', state: loc.state || '', pincode: loc.pincode || '', phone: loc.phone || '', email: loc.email || '', is_head_office: loc.is_head_office || false, status: loc.status });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) return;
    try {
      const result = await locationsService.deleteLocation(id);
      if (result?.success) {
        setNotification({ message: 'Location deleted successfully', type: 'success' });
        loadLocations();
      } else {
        setNotification({ message: result?.error || 'Failed to delete', type: 'error' });
      }
    } catch (error) {
      setNotification({ message: 'An error occurred', type: 'error' });
    }
  };

  return (
    <div className="p-6">
      <NotificationToast message={notification?.message} type={notification?.type} onDismiss={() => setNotification(null)} />
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Locations</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage office locations</p>
        </div>
        <Button onClick={() => { setEditingLoc(null); setFormData({ name: '', address: '', city: '', state: '', pincode: '', phone: '', email: '', is_head_office: false, status: 'active' }); setShowModal(true); }}>
          <Icon name="Plus" size={16} className="mr-2" />
          Add Location
        </Button>
      </div>
      <div className="mb-4">
        <Input placeholder="Search locations..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full max-w-md" iconName="Search" />
      </div>
      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" /><p className="text-muted-foreground">Loading...</p></div>
      ) : locations.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Icon name="MapPin" size={48} className="mx-auto mb-4 opacity-50" />
          <p>No locations found</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    City, State, PIN
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {locations.map((loc) => (
                  <tr key={loc.id} className="hover:bg-muted/50 transition-colors">
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Icon name="MapPin" size={20} className="text-green-600" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {loc.name}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-sm text-foreground max-w-[200px] truncate">
                        {loc.address || 'N/A'}
                      </p>
                    </td>
                    <td className="px-4 py-4 text-sm text-foreground">
                      {[loc.city, loc.state, loc.pincode].filter(Boolean).join(', ') || 'N/A'}
                    </td>
                    <td className="px-4 py-4">
                      <div className="space-y-1">
                        {loc.phone && (
                          <div className="flex items-center gap-2">
                            <Icon name="Phone" size={14} className="text-muted-foreground" />
                            <span className="text-sm text-foreground">{loc.phone}</span>
                          </div>
                        )}
                        {loc.email && (
                          <div className="flex items-center gap-2">
                            <Icon name="Mail" size={14} className="text-muted-foreground" />
                            <span className="text-sm text-foreground truncate max-w-[150px]">{loc.email}</span>
                          </div>
                        )}
                        {!loc.phone && !loc.email && <span className="text-sm text-muted-foreground">N/A</span>}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {loc.is_head_office ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          Head Office
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Branch</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        loc.status === 'active' 
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' 
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {loc.status || 'active'}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(loc)}
                          title="Edit"
                        >
                          <Icon name="Edit" size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(loc.id)}
                          className="text-destructive"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowModal(false); setEditingLoc(null); }}>
          <div className="bg-background rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">{editingLoc ? 'Edit' : 'Add'} Location</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <Input label="Name *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              <textarea value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md" rows="2" placeholder="Address" />
              <div className="grid grid-cols-2 gap-3">
                <Input label="City" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} />
                <Input label="State" value={formData.state} onChange={(e) => setFormData({...formData, state: e.target.value})} />
              </div>
              <Input label="PIN Code" value={formData.pincode} onChange={(e) => setFormData({...formData, pincode: e.target.value})} />
              <div className="grid grid-cols-2 gap-3">
                <Input label="Phone" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} />
                <Input label="Email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} />
              </div>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={formData.is_head_office} onChange={(e) => setFormData({...formData, is_head_office: e.target.checked})} />
                <span className="text-sm">Head Office</span>
              </label>
              <select value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full px-3 py-2 border border-border rounded-md">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={() => { setShowModal(false); setEditingLoc(null); }}>Cancel</Button>
                <Button type="submit">Save</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Locations;

